import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  paymentSessionExpired,
  paymentStatus,
} from "../../../../services/billing";
import { useRazorpay } from "../../../../context/RazorpayContext";
import Hourglass3D from "./Hourglass";

function RazorpayModal({ navigationUrl }) {
  const navigate = useNavigate();
  const currentPath = useLocation().pathname;
  const {
    razorpayModalData,
    handleFailed,
    setRazorpayModalData,
    isRazorpayModalTimerLoader,
    setIsRazorpayModalTimerLoader,
    setPaymentStatus,
  } = useRazorpay();
  const TOTAL_TIME = 15 * 60;
  const [timeLeft, setTimeLeft] = useState(TOTAL_TIME);
  const [modalData, setModalData] = useState({
    open: false,
    type: "",
    message: "",
  });

  const failedNavigationUrl = !razorpayModalData?.isPaymentExpired && "/marketplace-accounts"

  useEffect(() => {
    if (!razorpayModalData?.invoiceId) {
      return;
    }
    if (!localStorage.getItem("razorpayModalData")) {
      localStorage.setItem(
        "razorpayModalData",
        JSON.stringify(razorpayModalData)
      );
    }
    setIsRazorpayModalTimerLoader(true);
    let startTime = localStorage.getItem("paymentStartTime");
    if (!startTime) {
      startTime = Date.now();
      localStorage.setItem("paymentStartTime", startTime);
    }

    const elapsed = Math.floor((Date.now() - Number(startTime)) / 1000);
    const remaining = TOTAL_TIME - elapsed;
    setTimeLeft(remaining > 0 ? remaining : 0);

    // Store interval IDs so we can clear them later
    let statusInterval, timerInterval;

    // Payment status check every 5 seconds
    statusInterval = setInterval(async () => {
      try {
        const res = await paymentStatus(razorpayModalData?.invoiceId);
        if (res?.data?.data?.status === "paid") {
          clearInterval(statusInterval);
          clearInterval(timerInterval);
          setModalData({
            open: true,
            type: "success",
            message: "🎉 Your transaction is complete! Click below to continue.",
          });
          setIsRazorpayModalTimerLoader(false);
          setPaymentStatus("success");
          localStorage.removeItem("razorpayModalData");
          localStorage.removeItem("paymentStartTime");
        } else if (res?.data?.data?.status === "failed") {
          clearInterval(statusInterval);
          clearInterval(timerInterval);
          setPaymentStatus("failed");
          setIsRazorpayModalTimerLoader(false);
          setModalData({
            open: true,
            type: "failed",
            message: "❌ Payment Failed. Please try again.",
          });
          localStorage.removeItem("razorpayModalData");
          localStorage.removeItem("paymentStartTime");
        }
      } catch (err) {
        console.error("Payment status check failed:", err);
      }
    }, 5000);

    // Timer countdown
    timerInterval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handlePaymentExpired(statusInterval, timerInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Warn user before closing/refreshing
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue =
        "⚠️ Do not refresh or close this page, otherwise payment may fail!";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      clearInterval(statusInterval);
      clearInterval(timerInterval);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [razorpayModalData?.invoiceId, navigationUrl]);

  // Handle payment expiry
  const handlePaymentExpired = async (statusInterval, timerInterval) => {
    try {
      if (razorpayModalData?.isPaymentExpired) {
        const paramsData = {
          invoice_id: razorpayModalData?.invoiceId,
          reason: "time expired",
        };
        await paymentSessionExpired(paramsData);
      }
      setPaymentStatus("expired");
      setModalData({
        open: true,
        type: "expired",
        message: "⌛ Payment session expired. Please try again.",
      });
      localStorage.removeItem("razorpayModalData");
      localStorage.removeItem("paymentStartTime");
    } catch (err) {
      console.error("Failed to call payments time_expired API:", err);
    } finally {
      clearInterval(statusInterval);
      clearInterval(timerInterval);
      setIsRazorpayModalTimerLoader(false);
    }
  };

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handlePaymentSuccessNavigation = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    localStorage.removeItem("razorpayModalData");
    localStorage.removeItem("paymentStartTime");

    setRazorpayModalData((prev) => ({
      ...prev,
      shortUrl: null,
      invoiceId: null,
      isLoading: false,
      isRazorPayModal: false,
      isBlockedRedirection: false,
    }));
    setIsRazorpayModalTimerLoader(false);
    await setModalData({ open: false, type: "", message: "" });
    if (currentPath === navigationUrl) {
      if (typeof razorpayModalData?.refetchDataFn === "function") {
        razorpayModalData.refetchDataFn();
        setRazorpayModalData((prev) => ({
          ...prev,
          refetchDataFn: null,
        }));
      } else {
        await delay(200);
        window.dispatchEvent(new Event("refresh-page"));
      }
    } else {
      await navigate(navigationUrl, { replace: true });
    }
  };

  return (
    <>
      {isRazorpayModalTimerLoader ? (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            color: "white",
            textAlign: "center",
          }}
        >
          {/* Circular Loader */}
          <div
            className="spinner-border text-warning"
            style={{ width: "4rem", height: "4rem", marginBottom: "20px" }}
            role="status"
          ></div>

          <p
            style={{
              color: "orange",
              fontWeight: "bold",
              marginBottom: "15px",
            }}
          >
            ⚠️ Do not refresh or close this page during payment!
          </p>

          <h3 style={{ fontWeight: "bold", marginBottom: "10px" }}>
            Processing Payment...
          </h3>
          <p style={{ fontSize: "1.2rem" }}>
            Please wait. Your payment session will expire in:
          </p>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              fontWeight: "bold",
              background: "rgba(255,255,255,0.1)",
              padding: "10px 20px",
              borderRadius: "10px",
              marginTop: "10px",
            }}
          >
            <Hourglass3D /> {formatTime(timeLeft)}
          </div>

          {/* 🔥 Blocked Redirection Message */}
          {razorpayModalData?.isBlockedRedirection && (
            <div
              style={{
                marginTop: "25px",
                background: "rgba(255,255,255,0.1)",
                padding: "15px 20px",
                borderRadius: "10px",
                maxWidth: "600px",
              }}
            >
              <p style={{ color: "#ffcc00", marginBottom: "10px" }}>
                <i className="bi bi-ban stylish-ban"></i> Redirection Blocked or
                Refresh Detected!
              </p>
              <p style={{ marginBottom: "10px" }}>
                Please click the link below to complete your payment manually:
              </p>
              <a
                href={razorpayModalData?.shortUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#00bfff",
                  textDecoration: "underline",
                  wordBreak: "break-all",
                  fontWeight: "bold",
                }}
              >
                {razorpayModalData?.shortUrl}
              </a>
              <p style={{ marginTop: "10px", color: "#aaa" }}>
                (We’ve also sent this link to your registered email and phone.)
              </p>
            </div>
          )}
        </div>
      ) : modalData.open ? (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3000,
            animation: "fadeIn 0.3s ease-in-out",
          }}
        >
          <div
            style={{
              background: "white",
              color: "black",
              padding: "30px",
              borderRadius: "16px",
              textAlign: "center",
              width: "380px",
              boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
            }}
          >
            <div style={{ fontSize: "50px", marginBottom: "10px",border: "none" }}>
              {modalData.type === "success"
                ? <div className="success-icon">
                    <svg className="checkmark" viewBox="0 0 52 52">
                        <polyline points="14 27 22 35 38 17"/>
                    </svg>
                </div>
                : modalData.type === "failed"
                ? "❌"
                : "⌛"}
            </div>

              <h2
                style={{
                  marginBottom: "15px",
                  background:
                    modalData.type === "success"
                      ? "linear-gradient(135deg, #10b981 0%, #059669 100%)" // green gradient
                      : modalData.type === "failed"
                      ? "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)" // red gradient
                      : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)", // orange gradient
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
              {modalData.type === "success"
                ? "Payment Successful"
                : modalData.type === "failed"
                ? "Payment Failed"
                : "Session Expired"}
            </h2>
            <p style={{ fontSize: "1.1rem", marginBottom: "20px" }}>
              {modalData.message}
            </p>

            {modalData.type === "success" ? (
              <button
                onClick={handlePaymentSuccessNavigation}
                style={{
                  padding: "12px 24px",
                  background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "bold",
                  width: "100%",
                }}
              >
                Continue
              </button>
            ) : (
              <button
                onClick={()=>handleFailed(failedNavigationUrl)}
                style={{
                  padding: "12px 24px",
                  background: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "bold",
                  width: "100%",
                }}
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      ) : (
        <div>Redirecting to payment page...</div>
      )}
    </>
  );
}

export default RazorpayModal;
